import {
  ArrowSquareOut,
  Clock,
  MagnifyingGlass,
  MapPin,
  NavigationArrow,
  Phone,
  X,
} from '@phosphor-icons/react';
import { distanceKm, distanceLabel, hoursLabel, routeLink, type Spot } from '@shh/shared';
import { Icon, IconButton, Text, cn } from '@shh/ui';
import { useEffect, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useFamily } from '../data/family';
import { searchSpots, spotDetails, type SpotHit } from '../lib/spots';
import { locale } from '../lib/locale';
import { funText } from '../i18n/fun';

const link =
  'inline-flex min-h-touch-min items-center gap-gap-xs rounded-control font-semibold underline underline-offset-2 outline-none focus-visible:outline-3 focus-visible:outline-border-focus';

/**
 * What the family needs to know about a destination: where, how far from home, when it is open,
 * and one tap to the route (Google Maps) or the website.
 */
export function SpotInfo({ spot, compact }: { spot: Spot; compact?: boolean }) {
  const f = useFamily();
  const home = f.household?.location;
  const far = home
    ? distanceLabel(distanceKm({ lat: home.latitude, lon: home.longitude }, spot), locale)
    : undefined;
  return (
    <div className="flex flex-col gap-gap-xs text-sm">
      <span className="flex items-start gap-gap-xs">
        <Icon icon={MapPin} size="sm" className="mt-0.5 shrink-0 text-accent-secondary" />
        <span>
          {compact
            ? (spot.address ?? spot.name)
            : [spot.name, spot.address].filter(Boolean).join(' · ')}
          {far && <span className="text-text-secondary"> · {far}</span>}
        </span>
      </span>
      {spot.hours && (
        <span className="flex items-start gap-gap-xs">
          <Icon icon={Clock} size="sm" className="mt-0.5 shrink-0 text-accent-secondary" />
          <span>{hoursLabel(spot.hours, locale)}</span>
        </span>
      )}
      <span className="flex flex-wrap gap-x-gap-md">
        <a href={routeLink(spot)} target="_blank" rel="noreferrer noopener" className={link}>
          <Icon icon={NavigationArrow} size="sm" /> {funText.route}
        </a>
        {spot.website && (
          <a href={spot.website} target="_blank" rel="noreferrer noopener" className={link}>
            <Icon icon={ArrowSquareOut} size="sm" /> {funText.website}
          </a>
        )}
        {spot.phone && (
          <a href={`tel:${spot.phone.replace(/[^\d+]/g, '')}`} className={link}>
            <Icon icon={Phone} size="sm" /> {funText.call}
          </a>
        )}
      </span>
    </div>
  );
}

/**
 * Typing a destination finds it on the map: tap a hit and its address, distance, opening hours
 * and website are taken over. Nothing found → the idea simply stays a name.
 */
export function SpotSearch({
  query,
  spot,
  onPick,
  onClear,
}: {
  query: string;
  spot?: Spot;
  onPick: (picked: { spot: Spot; hit: SpotHit; free: boolean }) => void;
  onClear: () => void;
}) {
  const f = useFamily();
  const home = f.household?.location;
  const [hits, setHits] = useState<SpotHit[]>([]);
  const [searched, setSearched] = useState('');
  const [loading, setLoading] = useState<string | null>(null);
  const q = query.trim();
  const active = !spot && q.length >= 3;

  useEffect(() => {
    if (!active) return;
    let alive = true;
    // wait until the typing pauses – one request per word, not per letter
    const t = window.setTimeout(() => {
      searchSpots(q, home)
        .then((h) => alive && (setHits(h), setSearched(q)))
        .catch(() => alive && (setHits([]), setSearched(q)));
    }, 300);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [q, active, home]);

  if (spot) {
    return (
      <div className="flex items-start gap-gap-sm rounded-card bg-surface-sunken p-inset-sm">
        <div className="min-w-0 flex-1">
          <SpotInfo spot={spot} />
        </div>
        <IconButton icon={X} aria-label={funText.removePlace} onPress={onClear} />
      </div>
    );
  }
  if (!active || searched !== q) return null;
  if (!hits.length) {
    return (
      <Text size="sm" tone="muted" className="flex items-center gap-gap-xs">
        <Icon icon={MagnifyingGlass} size="sm" /> {funText.nothingOnMap}
      </Text>
    );
  }
  return (
    <section aria-label={funText.foundOnMap} className="flex flex-col gap-gap-xs">
      <span className="text-sm font-semibold text-text-secondary">{funText.foundHint}</span>
      <ul className="flex flex-col gap-gap-xs">
        {hits.map((h) => {
          const far = home
            ? distanceLabel(
                distanceKm({ lat: home.latitude, lon: home.longitude }, h),
                locale,
              ).split(' · ')[0]
            : undefined;
          return (
            <li key={h.osm}>
              <AriaButton
                isDisabled={loading !== null}
                onPress={() => {
                  setLoading(h.osm);
                  void spotDetails(h).then(({ spot: s, free }) => {
                    setLoading(null);
                    onPick({ spot: s, hit: h, free });
                  });
                }}
                className={cn(
                  'flex min-h-touch-min w-full cursor-pointer items-center gap-gap-sm rounded-control border border-border-subtle bg-surface-raised px-inset-sm py-inset-xs text-left outline-none',
                  'data-hovered:border-border-default data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-98',
                )}
              >
                <Icon icon={MapPin} size="md" className="shrink-0 text-accent-secondary" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold">{h.name}</span>
                  <span className="truncate text-sm text-text-secondary">
                    {[h.kind, h.town, far].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {loading === h.osm && (
                  <span className="text-sm text-text-muted">{funText.loading}</span>
                )}
              </AriaButton>
            </li>
          );
        })}
      </ul>
      <span className="text-xs text-text-muted">{funText.osm}</span>
    </section>
  );
}
